import {
    computed,
    defineComponent
} from "vue";
import { useVuePublicRouteData } from "@logscord/public-site/adapters/vue";
import { PublicArticlePageResponseSchema } from "@logscord/public-site-config";
import { MarkdownDoc } from "./MarkdownDoc.js";
import {
    PublicFooter,
    PublicNavbar,
    usePublicLocalizedPath,
} from "./PublicShell.js";
import { useLocale } from "../../locales/i18n.js";


//////////////////////////////////////////////////
//  DOC PAGE
//////////////////////////////////////////////////


export default defineComponent({
    setup() {
        const { i18n } = useLocale();
        const publicPath = usePublicLocalizedPath();
        const publicRouteData = useVuePublicRouteData();
        const page = computed(() => {
            const value = publicRouteData.getData("publicArticlePage");
            const result = PublicArticlePageResponseSchema.safeParse(value);

            return result.success
                ? result.data
                : null;
        });
        const notFound = computed(() => publicRouteData.state.value?.status === "not-found");
        const unavailable = computed(() => {
            const status = publicRouteData.state.value?.status;

            return status === "unavailable"
                || (status === "ready" && page.value === null);
        });
        const loading = computed(() => (
            publicRouteData.state.value?.status === "loading"
            || publicRouteData.state.value?.status === "idle"
        ));

        return () => {
            const currentPage = page.value;
            const currentArticle = currentPage?.article;

            if (notFound.value) {
                return (
                    <div class="public-page">
                        <PublicNavbar />

                        <div class="public-doc-not-found">
                            <span class="landing-kicker">{i18n("docs.not_found.kicker")}</span>
                            <h1>{i18n("docs.not_found.title")}</h1>
                            <p>{i18n("docs.not_found.description")}</p>

                            <router-link class="landing-button landing-button-dark" to={publicPath("/docs")}>
                                {i18n("docs.not_found.action")}
                                <i class="material-symbols-outlined">arrow_back</i>
                            </router-link>
                        </div>

                        <PublicFooter />
                    </div>
                );
            }

            if (unavailable.value) {
                return (
                    <div class="public-page">
                        <PublicNavbar />
                        <div class="public-doc-not-found">
                            <span class="landing-kicker">{i18n("docs.not_found.kicker")}</span>
                            <h1>{i18n("docs.unavailable.title")}</h1>
                            <p>{i18n("docs.unavailable.description")}</p>
                        </div>
                        <PublicFooter />
                    </div>
                );
            }

            if (!currentArticle) {
                return (
                    <div class="public-page">
                        <PublicNavbar />
                        <div class="public-doc-layout">
                            <div class="public-doc-article">
                                {loading.value && <div class="manual-card manual-blog-card" />}
                            </div>
                        </div>
                        <PublicFooter />
                    </div>
                );
            }

            return (
                <div class="public-page">
                    <PublicNavbar />

                    <div class="public-doc-layout">
                        <div class="public-doc-sidebar">
                            <router-link class="public-doc-back" to={publicPath("/docs")}>
                                <i class="material-symbols-outlined">arrow_back</i>
                                {i18n("docs.all_manuals")}
                            </router-link>

                            <div class="public-doc-sidebar-title">{i18n("docs.sidebar.documentation")}</div>

                            {currentPage.articles.map((article) => (
                                <router-link class="public-doc-sidebar-link" to={publicPath(`/docs/${article.slug}`)}>
                                    <i class="material-symbols-outlined">{article.icon}</i>
                                    <span>{article.title}</span>
                                </router-link>
                            ))}
                        </div>

                        <div class="public-doc-article">
                            <div class="public-doc-header">
                                <div class="manual-card-icon">
                                    <i class="material-symbols-outlined">{currentArticle.icon}</i>
                                </div>

                                <span class="landing-kicker">{currentArticle.category} · {currentArticle.readTime}</span>
                                <h1>{currentArticle.title}</h1>
                                <p>{currentArticle.description}</p>
                            </div>

                            <MarkdownDoc markdown={currentArticle.markdown} />
                        </div>
                    </div>

                    <PublicFooter />
                </div>
            );
        };
    },
});
